import type { ClassScheduleEntry } from "@/openapi/generated";
import { formatTimeTo12Hour } from "@/lib/utils";
import { formatDateOnlySafe } from "@/lib/utils/date/date";
import { toCivilYmd } from "@/lib/utils/date/date-only";
import { parseYmdLocal } from "@/lib/utils/date/formatYmdLocal";
import {
  calendarDaysBetweenYmd,
  formatStudioHHmm,
  formatYmdInTimeZone,
} from "@/lib/utils/date/studio-clock";
import { makeupDateOnlyYmd } from "./makeupDates";

export type ScheduleInstantSlot = {
  start: Date | string;
  end: Date | string;
  /** OpenAPI `format: date` may deserialize as `Date` during CI client regen. */
  sessionDate?: string | Date;
  sessionStartTime?: string;
  sessionEndTime?: string;
};

function sessionDateToYmd(value: string | Date): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return trimmed.slice(0, 10);
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return null;
    return toCivilYmd(parsed);
  }
  if (Number.isNaN(value.getTime())) return null;
  return toCivilYmd(value);
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

export function getScheduleEntryWallClock(
  entry: ScheduleInstantSlot,
): {
  dateYmd: string;
  startTime: string;
  endTime: string;
} | null {
  const dateYmd = entry.sessionDate
    ? sessionDateToYmd(entry.sessionDate)
    : null;
  if (!dateYmd || !entry.sessionStartTime || !entry.sessionEndTime) {
    return null;
  }

  return {
    dateYmd,
    startTime: normalizeMakeupTimeOnly(entry.sessionStartTime),
    endTime: normalizeMakeupTimeOnly(entry.sessionEndTime),
  };
}

export function scheduleInstantYmd(
  value: Date | string,
  timeZone?: string | null,
): string | null {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatYmdInTimeZone(date, timeZone);
}

export function scheduleInstantTimeOnly(
  value: Date | string,
  timeZone?: string | null,
): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return "00:00:00";
  return `${formatStudioHHmm(date, timeZone)}:00`;
}

export function dateOnlyFromScheduleInstant(
  value: Date | string,
  timeZone?: string | null,
): Date {
  const ymd = scheduleInstantYmd(value, timeZone);
  if (!ymd) return new Date(NaN);
  return parseYmdLocal(ymd);
}

export function normalizeMakeupTimeOnly(value: string | null | undefined): string {
  if (!value) return "";
  const parts = value.trim().split(":");
  const hour = (parts[0] ?? "00").padStart(2, "0");
  const minute = (parts[1] ?? "00").padStart(2, "0");
  const second = (parts[2] ?? "00").slice(0, 2).padStart(2, "0");
  return `${hour}:${minute}:${second}`;
}

export function formatScheduleSlotDateLabel(
  value: Date | string,
  timeZone?: string | null,
): string {
  const ymd = scheduleInstantYmd(value, timeZone);
  if (!ymd) return "-";
  return formatDateOnlySafe(ymd, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function formatScheduleSlotDateLabelFromEntry(
  entry: ScheduleInstantSlot,
  timeZone?: string | null,
): string {
  const wall = getScheduleEntryWallClock(entry);
  if (wall?.dateYmd) {
    return formatDateOnlySafe(wall.dateYmd, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
  return formatScheduleSlotDateLabel(entry.start, timeZone);
}

export function formatScheduleSlotTimeRange(
  start: Date | string,
  end: Date | string,
  timeZone?: string | null,
): string {
  const startLabel = formatTimeTo12Hour(scheduleInstantTimeOnly(start, timeZone));
  const endLabel = formatTimeTo12Hour(scheduleInstantTimeOnly(end, timeZone));
  return `${startLabel} – ${endLabel}`;
}

export function formatScheduleSlotTimeRangeFromEntry(
  entry: ScheduleInstantSlot,
  timeZone?: string | null,
): string {
  const wall = getScheduleEntryWallClock(entry);
  if (wall) {
    return `${formatTimeTo12Hour(wall.startTime)} – ${formatTimeTo12Hour(wall.endTime)}`;
  }
  return formatScheduleSlotTimeRange(entry.start, entry.end, timeZone);
}

export function dateOnlyFromScheduleEntry(
  entry: ScheduleInstantSlot,
  timeZone?: string | null,
): Date {
  const wall = getScheduleEntryWallClock(entry);
  if (wall?.dateYmd) return parseYmdLocal(wall.dateYmd);
  return dateOnlyFromScheduleInstant(entry.start, timeZone);
}

export function makeupWeeksLaterLabel(
  fromDate: Date | string | null | undefined,
  toInstant: Date | string,
  timeZone?: string | null,
): string | null {
  const fromYmd = makeupDateOnlyYmd(fromDate, timeZone);
  const toYmd = scheduleInstantYmd(toInstant, timeZone);
  if (!fromYmd || !toYmd) return null;

  const days = calendarDaysBetweenYmd(fromYmd, toYmd);
  if (days <= 0 || days % 7 !== 0) return null;

  const weeks = days / 7;
  if (weeks === 1) return "Next week";
  return `In ${weeks} weeks`;
}

export function initialScheduleMonthFromReference(
  today: Date,
  referenceDate?: Date | string | null,
): { month: number; year: number } {
  const todayMonth = today.getMonth() + 1;
  const todayYear = today.getFullYear();
  const referenceYmd = makeupDateOnlyYmd(referenceDate);
  if (!referenceYmd) return { month: todayMonth, year: todayYear };

  const [year, month] = referenceYmd.split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return { month: todayMonth, year: todayYear };
  }

  const referenceIndex = year * 12 + (month - 1);
  const todayIndex = todayYear * 12 + (todayMonth - 1);
  if (referenceIndex > todayIndex) return { month, year };
  return { month: todayMonth, year: todayYear };
}

export function scheduleSlotAriaLabel(params: {
  className: string;
  start: Date | string;
  end: Date | string;
  weekLabel?: string | null;
  timeZone?: string | null;
}): string {
  const dateLabel = formatScheduleSlotDateLabel(params.start, params.timeZone);
  const timeLabel = formatScheduleSlotTimeRange(
    params.start,
    params.end,
    params.timeZone,
  );
  const week = params.weekLabel ? `, ${params.weekLabel}` : "";
  return `${params.className}, ${dateLabel}, ${timeLabel}${week}`;
}

export function toSchedulePayload(entry: ClassScheduleEntry, timeZone?: string | null) {
  const wall = getScheduleEntryWallClock(entry);
  if (wall) {
    return {
      suggestedDate: parseYmdLocal(wall.dateYmd),
      suggestedStartTime: wall.startTime,
      suggestedEndTime: wall.endTime,
    };
  }

  const start = toDate(entry.start);
  const end = toDate(entry.end);
  return {
    suggestedDate: dateOnlyFromScheduleInstant(start, timeZone),
    suggestedStartTime: scheduleInstantTimeOnly(start, timeZone),
    suggestedEndTime: scheduleInstantTimeOnly(end, timeZone),
  };
}

export function scheduleEntryStartTime(
  entry: ScheduleInstantSlot,
  timeZone?: string | null,
): string {
  const wall = getScheduleEntryWallClock(entry);
  if (wall) return wall.startTime;
  return scheduleInstantTimeOnly(entry.start, timeZone);
}

export function scheduleEntryEndTime(
  entry: ScheduleInstantSlot,
  timeZone?: string | null,
): string {
  const wall = getScheduleEntryWallClock(entry);
  if (wall) return wall.endTime;
  return scheduleInstantTimeOnly(entry.end, timeZone);
}
